import { createRoutine } from 'redux-saga-routines';
import { takeLatest, call, put, select } from 'redux-saga/effects';
import { get } from 'lodash/fp';
// @ts-ignore ts-migrate(7016) FIXME: Could not find a declaration file for module 'quer... Remove this comment to see the full error message
import queryString from 'query-string';
import api, { API_HOST } from '../helpers/api';
import { STATUS_CONFIRMED, STATUS_CANCELLED, SEARCH_TYPE } from '../helpers/constants';
import { IBooking, IGetBookingPayload, IUser } from '../helpers/types';

export const getCustomerBookings = createRoutine('bookings/GET_CUSTOMER_BOOKINGS');
export const getBooking = createRoutine('bookings/GET_BOOKING');
export const updateBooking = createRoutine('bookings/UPDATE_BOOKING');
export const cancelBooking = createRoutine('bookings/CANCEL_BOOKING');
export const clearBookings = createRoutine('bookings/CLEAR_BOOKINGS');
export const filterCustomerBookings = createRoutine('bookings/FILTER_CUSTOMER_BOOKING');

export const SAVE_BOOKING_ACTION_TYPE = 'bookings/SAVE_BOOKING/SUCCESS';

export function saveBooking({ locale, id, booking }: any) {
  return {
    type: SAVE_BOOKING_ACTION_TYPE,
    payload: { locale, id, booking },
  };
}

function* handleGetCustomerBookings({
  payload: { id, status = [STATUS_CONFIRMED, STATUS_CANCELLED], url = '' },
}: any) {
  try {
    const locale: string = yield select(get('intl.locale'));
    const searchParams = queryString.stringify({ language: locale, status: status.join('|') });
    yield put(getCustomerBookings.request());
    const { results, next } = yield call(
      api.get,
      url || `${API_HOST}/customers/${id}/bookings/?${searchParams}`,
      true,
    );
    let all: any = [];
    const bookings = results.reduce((allBookings: any, booking: any) => {
      all = [...all, booking.id];
      return { ...allBookings, [booking.id]: booking };
    }, {});
    yield put(
      getCustomerBookings.success({
        locale,
        bookings,
        all,
        next,
      }),
    );
  } catch (error) {
    yield put(getCustomerBookings.failure(error));
  } finally {
    yield put(getCustomerBookings.fulfill());
  }
}

function* handleFilterCustomerBookings({ payload: { id, searchTerm, searchType } }: any) {
  try {
    const locale: string = yield select(get('intl.locale'));

    yield put(filterCustomerBookings.request());
    const queryParam = {
      [searchType === SEARCH_TYPE.REF_NUMBER ? 'id' : 'attributes__tracking_reference']: searchTerm,
    };
    const { results, next } = yield call(
      api.get,
      `${API_HOST}/customers/${id}/bookings/?${new URLSearchParams(queryParam).toString()}`,
      true,
    );
    let all: any = [];
    const bookings = results.reduce((allBookings: any, booking: any) => {
      all = [...all, booking.id];
      return { ...allBookings, [booking.id]: booking };
    }, {});

    yield put(
      filterCustomerBookings.success({
        locale,
        bookings,
        all,
        next,
      }),
    );
  } catch (error) {
    yield put(filterCustomerBookings.failure(error));
  } finally {
    yield put(filterCustomerBookings.fulfill());
  }
}

function* handleGetBooking({
  payload: { bookingID: id, securityToken },
}: {
  payload: IGetBookingPayload;
  type: string;
}) {
  try {
    const locale: string = yield select(get('intl.locale'));
    const user: IUser = yield select(get('user.data'));
    const searchParams = new URLSearchParams({ language: locale });
    // only append security token for non-logged in usecase
    if (securityToken && !user) {
      searchParams.append('security_token', securityToken);
    }
    yield put(getBooking.request());
    const isAuthRequired = !!user || !securityToken;
    const booking: IBooking = yield call(api.get, `${API_HOST}/bookings/${id}/?${searchParams}`, isAuthRequired);
    yield put(getBooking.success({ locale, id, booking }));
  } catch (error) {
    yield put(getBooking.failure(error));
  } finally {
    yield put(getBooking.fulfill());
  }
}

function* handleUpdateBooking({ payload: { id, quotes } }: any) {
  try {
    const locale: string = yield select(get('intl.locale'));
    const searchParams = queryString.stringify({ language: locale });
    const body = JSON.stringify({ quotes });
    yield put(updateBooking.request());
    const booking: IBooking = yield call(api.patch, `${API_HOST}/bookings/${id}/?${searchParams}`, { body }, true);
    yield put(updateBooking.success({ locale, id, booking }));
  } catch (error) {
    yield put(updateBooking.failure(error));
  } finally {
    yield put(updateBooking.fulfill());
  }
}

function* handleCancelBooking({ payload: { id, quotes } }: any) {
  try {
    const locale: string = yield select(get('intl.locale'));
    const searchParams = queryString.stringify({ language: locale });
    const body = JSON.stringify({ quotes });
    yield put(cancelBooking.request());
    const booking: IBooking = yield call(api.post, `${API_HOST}/bookings/${id}/?${searchParams}`, { body }, true);
    yield put(cancelBooking.success({ locale, id, booking }));
  } catch (error) {
    yield put(cancelBooking.failure(error));
  } finally {
    yield put(cancelBooking.fulfill());
  }
}

export function* bookingsSaga() {
  yield takeLatest(getCustomerBookings.TRIGGER, handleGetCustomerBookings);
  yield takeLatest(getBooking.TRIGGER, handleGetBooking);
  yield takeLatest(updateBooking.TRIGGER, handleUpdateBooking);
  yield takeLatest(cancelBooking.TRIGGER, handleCancelBooking);
  yield takeLatest(filterCustomerBookings.TRIGGER, handleFilterCustomerBookings);
}
